/**
 * User: Rob Richard
 * Date: 2/11/14
 * Time: 1:15 PM
 */

"use strict";

var _ = require('underscore');
var $ = require('dibs-jquery-shim');
var SoaCollection = require('bunsen/collections/SoaCollection');
var solrHelper = require('bunsen/helpers/SolrHelper');
var assert = require('bunsen/helpers/assert');
var solrParamsSerialize = require('bunsen/helpers/solrParamsSerialize');
var SolrCollection;
var defaultParamOptions = {
    multiSelect : false,
    filterQuery : false,
    defaultParam : false,
    displayName : null,
    excludeFacet : false,
    excludeFromAll : false,
    min : false,
    max : false // both min and max cannot be true
};
var allFilterOptions = _.keys(defaultParamOptions);
var mustMatchOptions = ['filterQuery', 'min', 'max'];
var defaultGroupOptions = {
    name : null,
    typeOr : false,
    multiRestricted : false
};
var allGroupOptions = _.keys(defaultGroupOptions);

/**
 * @class SolrCollection
 * @extends SoaCollection
 * @property {string} filterQueryKey
 *     The key under which the filter query params are stored on this._params.
 *     Instead of a regular param object, this._params[this.filiterQueryKey] is a flushed out object that stores data
 *     about the filterQuery. It may contain "val", "excludeFacet" and/or "tagName"
 *     @example
 *     // Here is what this._params[this.filiterQueryKey] should look like :
 *     {
 *         key : {
 *             val : ['one', 'two'],
 *             excludeFacet : true,
 *             tagName : 'v',
 *             minMax : true    // in this case val[0] = min & val[1] = max
 *         },
 *         key1 : { ... }
 *     }
 *     // A note on excluding facets : when you apply a filter with the option {excludeFacet : true} -- the associated
 *     // filter query will be marked as excluded - which tells the fetchWithParameters() method to tag the filter {!tag=}
 *     // and use the !ex syntax to exclude that facet from filtering.
 */
SolrCollection = module.exports = SoaCollection.extend({

    initialize : function () {
        this._params = [];
        this._defaultParams = [];
        this._groups = [];
        return SolrCollection.__super__.initialize.call(this, arguments);
    },

    filterQueryKey: 'filterQuery',
    facetFieldKey: 'facetField',
    queryKey: 'query',
    commaDelimiter : '-COMMA-',
    _params : [],
    _defaultParams : [],
    _groups : [],

    parse : function (data) {
        data = SolrCollection.__super__.parse.call(this, data);

        this.facets = data.facets || null;

        // TODO ::: PUSH THIS LOGIC INTO THE PARENT SOA COLLECTION PARSE METHOD.
        return data.items || data;
    },

    /**
     * *** question*** Defaults {traditional : true} for SOA collection ... but why?
     * fetchWithParameters for SOLR collection uses the public method getQueryParams to generate the data to be sent
     * with the SOLR request. See notes for getgetQueryParams to see what special logic will be applied.
     *
     * @param {object} passedOptions
     * @returns {$.Deferred.promise}
     */
    fetchWithParameters : function (passedOptions) {
        var options = _.defaults(passedOptions || {}, {
            traditional: true,
            data: {}
        });
        options.data = _.extend(this.getQueryParams(), options.data);
        return SolrCollection.__super__.fetchWithParameters.call(this, options);
    },


    /**
     * HERE IS A LIST OF ALL THE THINGS THIS FUNCTION MUST DO :
     * - find all query params that are not filter queries and apply them simply using key / value-ness
     *     - they have to be grouped by their keys.
     * - do min / max logic -- so if you find a min / max then search for it's counterpart
     * -
     * @returns {{}}
     */
    getQueryParams : function () {
        var query = {};

        // First, get the regular params that are not filterQueries.
        this._getRegularParams(query);

        // Get the facets
        this._getFacetParams(query);

        // Get the filterQueries :
        this._getFQParams(query);

        return query;
    },


    /**
     * Adds the regular (non filterQuery / non facet) params to the query object.
     * Also adds a tag for the special query key.
     * @param query
     * @private
     */
    _getRegularParams : function (query) {
        _.each(this._params, function (param) {
            var key = param.key;
            var val = param.val;
            // Regular params are all the params that are not facets or filter queries.
            if (!param.options.filterQuery && param.key !== this.facetFieldKey) {
                var queryTagName = solrHelper.facetMap.searchQuery.tagName;
                var tagString = param.key === this.queryKey ? '{!tag=' + queryTagName + '}' : param.key;
                // If there is already a query by this key, then merge them, otherwise assign the val to the key.
                query[key] = query[key] ? _.flatten(_.union([query[key]], [val])) : val;
                // If this is the special "query" key, then add the tag so that it can be excluded by facets.
                if (param.key === this.queryKey) {
                    query[key] = tagString + query[key];
                }
            }
        }, this);
    },

    /**
     * Adds params for facets to the query object.
     * This does the special logic of applying exclusion tags to facets.
     * @param query
     * @private
     */
    _getFacetParams : function (query) {
        var facetKey = this.facetFieldKey;
        var exclusionMap = {};
        var excludeAllArray = [];

        // Filter the params down into facet params (
        var facetParams = _.filter(this._params, function (param) {
            var tag = solrHelper.getTagFromKey(param.key);
            var isExcludeAll = (param.options.filterQuery && param.options.excludeFromAll && !!tag);
            var isExcludeFacet = (param.options.filterQuery && param.options.excludeFacet && !!tag);
            exclusionMap[param.facetName] = exclusionMap[param.facetName] || [];

            if (isExcludeAll && excludeAllArray.indexOf(tag) === -1) {
                excludeAllArray.push(tag);
            }
            if (isExcludeFacet && !isExcludeAll && param.facetName) {
                exclusionMap[param.facetName].push(tag);
            }
            return param.key === facetKey;
        }, this);

        // If no facet params were found do nothig.
        if (!facetParams.length) {
            return;
        }

        // Else, map the facet params down into an array of strings, and add to the query :
        query[facetKey] = _.map(facetParams, function (param) {
            var excludeArray = _.uniq(_.union(excludeAllArray, (exclusionMap[param.val] || [])));
            var excludeTagString = excludeArray.length ? '{!ex=' + excludeArray.join(this.commaDelimiter) + '}' : '';
            return excludeTagString + param.val;
        }, this).join(',');
    },

    /**
     * This function creates the filter query param string based on filter query param settings.
     * The logic for filter queries takes into account the following :
     *      - taggin FQ params that are excluded
     *      - grouping params together based on groups and group type (typeOr)
     *      - syntax for range queries
     * @param query
     * @private
     */
    _getFQParams : function (query) {
        var filterQueryMap = {}; // a map of filter queries organized by groups.
        var out;

        function _organizeParam(groups, groupName, param) {
            var currGroup;
            var currParam;
            var group = _.findWhere(groups, {name : groupName});

            // First ensure that the proper place-holder for this param exists. If not then create the structure
            filterQueryMap[groupName] = filterQueryMap[groupName] || {};
            currGroup = filterQueryMap[groupName];
            currGroup.params = currGroup.params || {};

            // If a group was found for this group name, get tye typeOr data :
            currGroup.typeOr = (group && group.typeOr) ? true : false;

            // Now
            currGroup.params[param.key] = currGroup.params[param.key] || {};
            currParam = currGroup.params[param.key];
            currParam.vals = currParam.vals || [];

            // Add the key, value, range, and tag info to this entry in the map :
            currParam.key = param.key;
            currParam.vals.push(param.val);
            currParam.isMin = param.options.min;
            currParam.isMax = param.options.max;
            if (param.options.excludeFacet || param.options.excludeFromAll) {
                currParam.tag = solrHelper.getTagFromKey(param.key);
            }
        }

        // First, loop over the params, if the param is a filterquery, then organize into groups by group name or key.
        _.each(this._params, function (param) {
            if (!param.options.filterQuery) {
                return;
            }
            if (_.size(param.groups)) {
                // if there are groups, then add the param to each of the groups :
                _.each(param.groups, function (groupName) {
                    _organizeParam.apply(this, [this._groups, groupName, param]);
                }, this);
                // If there are no groups, then add this param to it's own group
            } else {
                _organizeParam.apply(this, [this._groups, param.key, param]);
            }
        }, this);

        // Now that params are all organized by group, loop through the groups and create filter queries for each param
        out = _.map(filterQueryMap, function (group) {
            var filterQueries = [];
            var delimiter = group.typeOr ? ' OR ' : ',';

            // Now for each param in this group (which may have many values) generate a filter query string :
            _.each(group.params, function (param) {
                filterQueries.push(this._makeFilterQueryString(param.key, param.vals, param.isMin, param.isMax, param.tag));
            }, this);

            if (_.size(group.params) > 1) {
                return '(' + filterQueries.join(delimiter) + ')';
            }

            return filterQueries.join('');

        // finally join the output array on the AND operator, a comma :
        }, this).join(',');

        // If we got any filter queries, return them all! ha ha ha ha ha ha ha ha!
        if (_.size(out)) {
            query[this.filterQueryKey] = out;
        }
    },

    /**
     * Generates the filter query string which is understood by services. Examples :
     *      l2_categories:(tables),vertical:(furniture)
     *      vertical:(furniture art)
     *      price:([10 TO 1000])
     *      {!tag=l2}l2_categories:(tables)
     *
     * @param key
     * @param vals
     * @param isMin
     * @param isMax
     * @param tag
     * @returns {string}
     * @private
     */
    _makeFilterQueryString : function (key, vals, isMin, isMax, tag) {
        var str = key + ':';
        var isRange = isMin || isMax;
        var bracket1 = isRange ? '([' : '(';
        var bracket2 = isRange ? '])' : ')';
        var delimiter = isRange ? ' TO ' : ' ';
        if (isRange && vals.length === 1) {
            vals = isMin ? [vals[0], '*'] : ['*', vals[0]];
        }
        str += bracket1;
        str += vals.join(delimiter);
        str += bracket2;
        if (tag) {
            return '{!tag=' + tag + '}' + str;
        }
        return str;
    },

    /**
     * Resets the params back to any params that were added as default params.
     * To take advantage of this use the option defaultParam
     */
    resetParams : function (paramsToApply) {
        this._params = [];
        _.each(this._defaultParams, function (param) {
            this.setParam(param.key, param.val, param.options);
        }, this);
        if (paramsToApply && _.isArray(paramsToApply)) {
            this.setParamsArray(paramsToApply);
        }
    },

    /**
     * Sets params just like on regular SOA collection - has added options you can use for filterQuery stuff.
     * @param {string} key
     * @param {string|number} value
     * @param {object} [passedOptions]
     * @param {boolean} [passedOptions.remove=true] alternate option to remove the passed param. You may also accomplish
     *      param removal by passing null as value.
     * @param {boolean} [passedOptions.defaultParam=true] denotes this is a default param. Will be applied on resetParams()
     * @param {boolean} [passedOptions.filterQuery=true] If you want the passed param to be a filter query (defaults false)
     * @param {boolean} [passedOptions.multiSelect=true] If the parameter being set is part of multi select (defaults false)
     * @param {boolean} [passedOptions.excludeFacet=true] Set the special flag to exclude this facet (from itself)
     * @param {boolean} [passedOptions.excludeFromAll=true] Set the special flag to exclude this facet from all facets.
     * @param {boolean} [passedOptions.min=true] If you want the variables to be formatted in min/max SOLR syntax
     * @param {boolean} [passedOptions.max=true] If you want the variables to be formatted in min/max SOLR syntax
     * @param {boolean} [passedOptions.remove=true]
     * @param {array} [passedOptions.groups] If you want to set a group
     * @param {string} [passedOptions.groups[0].name='groupName'] The group's name (defaults '')
     * @param {boolean} [passedOptions.groups[0].typeOr=true] Sets the relation between the values (defaults false)
     *      params with this group option turned on will be "OR"'d together. Params without will be "AND"'ed
     * @param {boolean} [passedOptions.groups[0].multiRestricted=true] Sets a restriction on the group that only 1
     *      param may be in the group at a time. Allows different parts of the code to clear other filters that are
     *      in the same group.
     */
    setParam : function (key, value, passedOptions) {
        var options = _.clone(passedOptions || {});

        // if value === null OR options.remove then remove all params matching this key.
        if (value === null || options.remove) {
            this._removeParams(this._findExistingParams(key, value, options)); // remove all params with this key
        // if this is not a multiSelect param, remove other filters for this key then add the param
        } else if (!options.multiSelect) {
            this._removeParams(this._findExistingParams(key, null, options)); // remove all params with this key
            this._addParam(key, value, options);
        // Otherwise rirst remove duplicate params then add the new param
        } else {
            this._removeParams(this._findExistingParams(key, value, options));
            this._addParam(key, value, options);
        }
    },

    /**
     * Helper function where we really add passed parameters from the setParam method (inverse of _removeParams)
     * Evaluates options based on special logic for multi select, facets, groups, min/max, etc.
     *
     * @param {string} key
     * @param {string|number} val
     * @param {object} options
     * @private
     */
    _addParam: function (key, val, options) {
        var addedGroups;
        var facets;
        var facetName;
        var associatedFacet;
        var filter = {
            key : key,
            val : val
        };

        if (_.isUndefined(val)) {
            throw new Error('You must pass a real value to set params on SOLR Collection');
        }

        if (options.min === true && options.max === true) {
            throw new Error('A given param may not be both min and max.');
        }

        // If groups were specified in the options, then add the groups.
        if (options.groups) {
            options.groups = addedGroups = this._addNewGroups(options.groups);
            filter.groups = _.map(addedGroups, function (group) {
                return group.name;
            });

            // If any of the groups for this param are multi-restricted, remove other params in the same group first :
            _.each(addedGroups, function (group) {
                var paramsInGroup = this.getParamsByGroup(group.name);
                if (group.multiRestricted) {
                    this._removeParams(paramsInGroup);
                }
            }, this);
        }

        // Apply defaults to the options. Only pay attention to params specified by allFilterOptions
        filter.options = _.pick(options, allFilterOptions);
        filter.options = _.defaults(options, defaultParamOptions);

        facetName = solrHelper.getNameFromKey(key);
        if (filter.options.filterQuery && this.facets && !!_.size(this.facets[facetName])) {
            facets = this.facets[facetName];
            associatedFacet = _.findWhere(facets, {name : val});
            filter.facetName = facetName;
            filter.facetKey = key;
            filter.associatedFacet = associatedFacet;
        }

        // If this is a default param, add it to the list of deftault params :
        if (options.defaultParam) {
            this._addDefaultParam(filter);
        }

        this._params.push(filter);
    },

    /**
     * Adds the default param by first removing matching default params.
     * @param filter
     * @private
     */
    _addDefaultParam : function (filter) {
        var searchOptions = _.pick(filter.options, mustMatchOptions);
        var matchingDefaultParams = this._filterParams(filter.key, filter.val, searchOptions, this._defaultParams);
        this._defaultParams = _.difference(this._defaultParams, matchingDefaultParams);
        this._defaultParams.push(filter);
    },

    /**
     * @param paramsToRemove
     * @private
     */
    _removeParams : function (paramsToRemove) {
        this._params = _.difference(this._params, paramsToRemove);
    },

    /**
     * Finds a group by it's (unique) name
     * @param groupName
     */
    getGroup : function (groupName) {
        return _.first(_.filter(this._groups, function (group) {
            return group.name === groupName;
        }));
    },

    /**
     * Returns all params that are in the specified group
     * @param groupName
     * @returns {*|Array}
     */
    getParamsByGroup : function (groupName) {
        return _.filter(this._params, function (param) {
            return param.groups && (param.groups.indexOf(groupName) !== -1);
        });
    },

    /**
     * Adds groups to the list of groups maintained by
     * When the user adds a new param (key / val pair) we call this function to add any new groups that may have been
     * passed in.
     *
     * @param groups
     * @returns Return the collection of groups that were added
     * @private
     */
    _addNewGroups : function (groups) {
        var newGroups = _.map(groups, function (group) {
            var cleanGroup = _.pick(group, allGroupOptions);
            return _.defaults(cleanGroup, defaultGroupOptions);
        });

        // Filter out duplicate groups being added :
        newGroups = _.uniq(newGroups, true, function (group) {
            return group.name;
        });

        // Remove out groups that already exist.
        this._groups = _.filter(this._groups, function (group) {
            var match = _.findWhere(newGroups, {name : group.name});
            return !match;
        }, this);

        // Combine groups with new groups :
        this._groups = _.union(this._groups, newGroups);

        return newGroups;
    },

    /**
     * Filter params on any setting including key, value, params. If you are using options, you may pass only the
     * ones you are concerned with. Other options will be ignored.
     * @param key
     * @param val
     * @param options
     * @returns {*|Array}
     */
    filterParams : function (key, val, options, params) {
        return this._filterParams(key, val, options, params);
    },

    /**
     * Private method to filter params. For public access, use "filterParams" -- this private method is here so
     * I can filter over default params AND other params.
     * This does not work for groups
     */
    _filterParams : function(key, val, options, paramsToFilter) {
        paramsToFilter = paramsToFilter || this._params;
        var _tester = function (param) {
            var hasKey = !!(key && key !== 0);
            var hasVal = !!(val && val !== 0);
            var hasOptions = !!(options && _.isObject(options));
            var optionKeys = hasOptions ? _.keys(options) : null;
            var optionsMatch = true;
            var nonMatchingParam;

            // First check the key.
            if (hasKey && key !== param.key) {
                return false;
            }

            // Reduce options via condition array to figure out if any don't match.
            // Note* This does not include group options. Only check passed options :
            if (hasOptions) {
                nonMatchingParam = _.find(optionKeys, function (option) {
                    return options[option] !== param.options[option];
                });
                optionsMatch = (nonMatchingParam === undefined);
            }

            // If any don't match return false.
            if (!optionsMatch) {
                return false;
            }

            // If value was passed, then evaluate whether it matches.
            if (hasVal) {
                return (param.val === val);
            }

            // Assuming all checks passed, return true;
            return true;
        };

        return _.filter(paramsToFilter, _tester);
    },

    /**
     * Finds existing applied paramaters by key, optional value, and passed options. For example, find params that
     * match a given key, and are filterQueries.
     *
     * This is the same as filterParams -- but it removes options from the search that don't differentiate the
     * param as distinct. For example, a param of derp=herp with option default param is the same param as derp=herp
     * with no default param option applied.
     *
     * *NOTE! This method may not be used without specifying a key to search by
     *
     * @param key
     * @param val
     * @param options <object> will be reduced to the folowing list : [ filterQuery, min, max ]
     * @returns {*|Array}
     */
    _findExistingParams : function (key, val, options) {
        var optionsToCheck = ['filterQuery', 'min', 'max'];
        options = _.pick(options, optionsToCheck);

        if (key === undefined) {
            throw new Error('You must supply a key to use _findExistingParams');
        }

        return this.filterParams(key, val, options);
    },


    /**
     * Searches the params based on key and options. Only certain options must match in order for the set params to
     * match including filterQuery, min and max. For example, you could do :
     *      collection.setParam('derp', 'nerp', {filterQuery : true});
     *      collection.getParams('derp', {filterQuery : true}); // length = 1
     *      collection.getParams('derp'); // length = 0
     * @param param
     * @param passedOptions
     * @returns {*}
     */
    getParams : function (param, passedOptions) {
        var options = passedOptions || {};
        var mustMatchDefaults = _.pick(defaultParamOptions, mustMatchOptions);
        // Apply default params to the search options so that every option will get compared.
        // NOTE* Here I ONLY apply the options that must match for the search to succeed. This included filterQuery,
        // min and max.
        options = passedOptions || {};
        options = _.pick(options, mustMatchOptions);
        options = _.defaults(options, mustMatchDefaults);
        var matchingParams = this.filterParams.apply(this, [param, null, options]);

        if (!matchingParams.length) {
            return null;
        }
        return _.map(matchingParams, function (param) {
            return param.val;
        });
    },

    /**
     * @deprecated @deprecated @deprecated @deprecated!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
     * Gets a param -- has the added benefit of being able to retrieve filterQuery params
     * This method will always return you only 1 param.
     * @deprecated!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! -- Please do not use instead please use getParams
     * @param {string} param
     * @param {object} passedOptions
     * @returns {*}
     */
    getParam : function (param, passedOptions) {
        var vals = this.getParams.apply(this, [param, passedOptions]);
        return vals && !_.isUndefined(vals[0]) ? vals[0] : undefined;
    },

    /**
     * Get an array of the current params that can be set back on the collection using setParamsArray
     */
    getCurrentParamsArray : function () {
        return _.map(this._params, function (param) {
            param = _.omit(param, 'groups');
            if (param.groups) {
                param.options.groups = [];
                _.each(param.groups, function (groupName) {
                    param.options.groups.push(this.getGroup(groupName));
                }, this);
            }
            return param;
        }, this);
    },

    /**
     * Convenience method for setting many params at once from an array. Array should be formatted like :
     * Each param in the params array should have a key, val, options (object)
     * @param paramsArray
     */
    setParamsArray : function (paramsArray) {
        assert(_.isArray(paramsArray), 'You must pass an array to use setParamsArray');
        _.each(paramsArray, function (param) {
            assert(_.has(param, 'key') && _.has(param, 'val'), 'Incorrectly configured param array.');
            var options = param.options || {};
            this.setParam(param.key, param.val, options);
        }, this);
    },

    /**
     * Find a facet based on a filterQuery key - val pair. ***Note - filter query keys are associated to their related
     * facets by the by the solrHelper utility.
     * @param {string} key filter query key
     * @param {string|number} val ilter query value
     * @returns {object}
     */
    geFacetFromFilterQuery : function (key, val) {
        // Find the facet you are specifying from the list of all facets.
        // get the name of the facet (ie. vertical) from the solr helper.
        var facetName = solrHelper.getNameFromKey(key);
        // get all the facets in this.facets with that facet name
        var associatedFacets = this.facets[facetName];

        // find the facet from the associatedFacets where the label matches the param value in this._params.filerQuery
        var currFacet = _.findWhere(associatedFacets, {
            label : val
        });

        return currFacet;
    },

    /**
     * Gets back display name stored on params
     * @param {string} key
     * @param {string|number} val
     * @param {object} options
     * @returns {string}
     */
    getDisplayName : function (key, val, options) {
        assert((!_.isUndefined(key) && !_.isUndefined(val)), 'You must pass a key and a val to use getDisplayName');

        // Only 1 param should be returned because key / val / options will get you a unique param.
        var params = this.filterParams(key, val, options);
        var param = _.first(params);
        return param ? param.options.displayName : null;
    },


    /**
     * Checks if a given parameter is applied. Options are the same as for setParam
     * @param {string} name
     * @param {string|number} val
     * @param options
     */
    checkParamApplied : function (key, val, options) {
        assert((!_.isUndefined(key) && !_.isUndefined(val)), 'You must pass a key and a val to use checkParamApplied');
        options = options || {};
        options = _.pick(options, mustMatchOptions);
        var params = this.filterParams(key, val, options);
        return !!params.length;
    },

    /**
     * Over-write the getTotalResultsForParams method from SOA Collection to work with SOLR Collection functionality.
     *
     * @param {object} passedParams
     * @param {function} callback
     * @param {object} options
     * @returns {$.Deferred}
     *
     * @example
     * // *** NOTE!! -- the structure of the params this method expects is different!!!!!!!! Please use something like :
     * {
     *      key : {
     *          val : 'string',
     *          options : { ... }
     *      },
     *      { ... }
     * }
     */
    getTotalResultsForParams: function (passedParams, callback, options) {

        if (typeof passedParams !== 'object') {
            throw new Error("You must pass a config object of param objects to use SOLR Collection's getTotalResultsForParams");
        }

        var totalCollection = new SolrCollection(null, { url: _.result(this, 'url') });

        totalCollection.setParamsArray(passedParams);

        totalCollection.setParam('pageStart', 0);
        totalCollection.setParam('pageSize', 0);
        totalCollection.setParam('returnTotalResults', true);
        // Don't pass this in since it may filter results
        totalCollection.setParam('orderBy', null, {remove : true});
        totalCollection.setParam('sort', null, {remove : true});

        var deferred = new $.Deferred();
        $.when(totalCollection.fetchWithParameters(options)).done(function () {
            if (callback) {
                callback(totalCollection.totalResults);
            }
            deferred.resolve(totalCollection.totalResults);
        }).fail(function () {
                deferred.reject();
            });
        return deferred;
    }

});


// STATIC METHODS --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
/**
 * Returns you a relative URL (for use in links) that can be deparam-ed later and fed back to the solr Collection.
 * @param paramsArray
 * @param base
 * @returns {*}
 */
SolrCollection.encodeParamsLink = function (paramsArray, base) {
    base = base || '';
    base = base.split('?')[0];
    var str = solrParamsSerialize.serializeParams(paramsArray);

    return base + '?query=' + encodeURIComponent(str);
};

/**
 * Decodes URI parameters that were encoded using the (above) encodeParamsLink method.
 * @param queryString
 * @returns {*}
 */
SolrCollection.decodeParamsLink = function (queryString) {
    var val;
    var arr;

    queryString = (queryString || window.location.search).replace(/^[^\?]*\?/, '') || '';
    val = queryString;

    arr = queryString.split('&');
    for (var i = 0; i < arr.length; i++) {
        var curr = arr[i].split('=');
        if (curr[0] === 'query') {
            val = curr[1];
        }
    }

    return solrParamsSerialize.deSerializeParams(decodeURIComponent(val));
};



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/collections/SolrCollection.js
// module id = 1207
// module chunks = 63